import React, { useRef } from 'react';
import { Bold, Italic, Heading, Code, Link, List, Quote, Type, Table as TableIcon } from 'lucide-react';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({ value, onChange, className = '' }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertFormat = (before: string, after: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selection = text.substring(start, end);
    
    // Insert text wrapper
    const newText = text.substring(0, start) + before + selection + after + text.substring(end);
    
    onChange(newText);
    
    // Reset focus and selection
    setTimeout(() => {
      textarea.focus();
      // If text was selected, keep it selected inside tags. If not, put cursor inside tags.
      const newCursorPos = start + before.length + selection.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertTable = () => {
    const tableTemplate = 
`\n| Header 1 | Header 2 | Header 3 |
| :--- | :--- | :--- |
| Row 1 Col 1 | Row 1 Col 2 | Row 1 Col 3 |
| Row 2 Col 1 | Row 2 Col 2 | Row 2 Col 3 |\n`;
    insertFormat(tableTemplate);
  };

  const ToolbarButton: React.FC<{ icon: React.ReactNode, label: string, onClick: () => void }> = ({ icon, label, onClick }) => (
    <button 
      type="button"
      onClick={onClick}
      className="p-2 text-slate-600 hover:text-brand-blue hover:bg-white hover:shadow-sm rounded transition-all"
      title={label}
    >
      {icon}
    </button>
  );

  return (
    <div className={`border border-slate-300 rounded-lg overflow-hidden bg-white ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-slate-100 bg-brand-slate">
        <ToolbarButton icon={<Heading className="w-4 h-4" />} label="H1" onClick={() => insertFormat('# ', '')} />
        <ToolbarButton icon={<Type className="w-4 h-4" />} label="H2" onClick={() => insertFormat('## ', '')} />
        <div className="w-px h-6 bg-slate-300 mx-1"></div>
        <ToolbarButton icon={<Bold className="w-4 h-4" />} label="Bold" onClick={() => insertFormat('**', '**')} />
        <ToolbarButton icon={<Italic className="w-4 h-4" />} label="Italic" onClick={() => insertFormat('*', '*')} />
        <div className="w-px h-6 bg-slate-300 mx-1"></div>
        <ToolbarButton icon={<Code className="w-4 h-4" />} label="Code" onClick={() => insertFormat('`', '`')} />
        <ToolbarButton icon={<Code className="w-4 h-4 stroke-[3px]" />} label="Block" onClick={() => insertFormat('\n```\n', '\n```\n')} />
        <div className="w-px h-6 bg-slate-300 mx-1"></div>
        <ToolbarButton icon={<Link className="w-4 h-4" />} label="Link" onClick={() => insertFormat('[', '](url)')} />
        <ToolbarButton icon={<Quote className="w-4 h-4" />} label="Quote" onClick={() => insertFormat('> ', '')} />
        <ToolbarButton icon={<List className="w-4 h-4" />} label="List" onClick={() => insertFormat('- ', '')} />
        {/* NEW TABLE BUTTON */}
        <ToolbarButton icon={<TableIcon className="w-4 h-4" />} label="Insert Table" onClick={insertTable} />
      </div>
      
      {/* Editor */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-4 min-h-[400px] focus:outline-none focus:ring-2 focus:ring-brand-blue/10 font-mono text-sm resize-y text-slate-800"
        placeholder="Write your masterpiece... (Markdown supported)"
      />
    </div>
  );
};